import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../config/firebaseConfig';
import InterviewLayout from './InterviewLayout';
import { useMicControl } from '../hooks/useMicControl';
import { getAvatarSpeech } from '../utils/avatarSpeechApi';
import { getMouthAssets } from '../utils/mouthAssetsApi';
import { fetchScriptDocument } from '../services/interviewService';
import script from '../script_denuncias_novo.json';
import { CORES, TIPOGRAFIA, FORMA } from '../utils/layoutOficial';

/**
 * MD1 — Canal de denúncia por avatar (Fase 1). Fork de
 * EntrevistaContinuaContainer.js, mas para o INTAKE do relato inicial (não
 * para a apuração de testemunhas): diferente da entrevista contínua (que
 * assume `denuncia`/`entrevistaId` já existentes, lidos da rota), aqui NADA
 * existe ainda — a sessão é criada por `iniciarDenunciaAvatar` na primeira vez
 * que a conversa precisa gravar algo (e não no mount: ver garantirSessao), e a
 * conversa segue turno a turno via `sendDenunciaAvatarTurn`. Reaproveita InterviewLayout e AvatarVisemePlayer
 * (via `videoProps.mode: 'viseme'`) sem nenhuma mudança — mesmo contrato já
 * usado por EntrevistaContinuaContainer.js/ChatContainerEntrevista.js.
 *
 * A saudação/aviso de confidencialidade inicial é o MESMO texto fixo já usado
 * pelo formulário legado (script_denuncias_novo.json / Denuncia.js), tocado
 * localmente (key estável 'intro-denuncia-avatar', cache-aside de verdade)
 * antes do primeiro turno real ser pedido ao backend — mesmo desenho de
 * introText/handleIntroOrTurnEnded de EntrevistaContinuaContainer.js.
 */
const DEFAULT_FEATURES = { captions: true, speed: false, audio: true, avatarPicker: false };
const INTERVIEW_TYPE_KEY = 'pesquisa-de-compliance';
const SCRIPT_VERSION = 'v1.2';
const INTRO_TEXT = (script?.denuncia?.[0]?.intro || '').toString().trim();

export default function DenunciaAvatarContainer() {
  const { company: companyFromRoute } = useParams();
  const companyKey = (companyFromRoute || 'materdei').toString().trim().toLowerCase();

  const [iniciado, setIniciado] = useState(false);
  const [scriptDoc, setScriptDoc] = useState(null);
  const [session, setSession] = useState(null); // {protocoloID, accessToken}
  const [sessionError, setSessionError] = useState('');

  const avatarCfg = scriptDoc?.script?.avatars?.[0] || null;

  const [messages, setMessages] = useState([]);
  const [currentTurn, setCurrentTurn] = useState(null); // {turnId, tipoResposta, escala, encerrar}
  const [avatarSpeech, setAvatarSpeech] = useState(null); // {audioSrc, visemes}
  const [mouthAssets, setMouthAssets] = useState(null);
  const [videoKey, setVideoKey] = useState(Date.now());
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const [isPlayingState, setIsPlayingState] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [canSend, setCanSend] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [errorText, setErrorText] = useState('');
  const videoRef = useRef(null);
  const introStartedRef = useRef(false);
  const sessaoRef = useRef(null); // Promise<{protocoloID, accessToken}> da criação sob demanda

  const { isListening, isTranscribing, transcript, toggleListening } = useMicControl();
  useEffect(() => {
    if (transcript) setUserInput(transcript);
  }, [transcript]);

  // A sessão da denúncia (protocoloID/accessToken) nasce SOB DEMANDA, na
  // primeira vez que a conversa precisa de um lugar para gravar — e não no
  // mount, como era até 19/09/2026.
  //
  // Por que mudou: `iniciarDenunciaAvatar` cria o documento da denúncia. Com a
  // chamada no mount, abrir a página já abria um protocolo, e a homologação de
  // produção provou isso do pior jeito: a captura de tela automática dessa rota
  // criou o protocolo 30525451 em due-prd, vazio, sem ninguém do outro lado.
  // O mesmo valeria para um rastreador de buscador ou para quem abre o link e
  // desiste. A saudação inicial é texto fixo tocado localmente, então nada
  // precisa do documento até o primeiro turno real.
  //
  // O ref guarda a PROMESSA, não o resultado: dois pedidos de turno quase
  // simultâneos compartilham a mesma criação em vez de abrirem dois protocolos.
  const garantirSessao = useCallback(() => {
    if (!sessaoRef.current) {
      const functions = getFunctions(app, 'southamerica-east1');
      const callable = httpsCallable(functions, 'iniciarDenunciaAvatar');
      sessaoRef.current = callable({ company: companyKey, interviewTypeKey: INTERVIEW_TYPE_KEY })
        .then(({ data }) => {
          const sessao = { protocoloID: data.protocoloID, accessToken: data.accessToken };
          setSession(sessao);
          return sessao;
        })
        .catch((error) => {
          console.error('[EIA] Falha ao iniciar denúncia por avatar:', error?.message || error);
          setSessionError('Não foi possível iniciar o canal de denúncia agora. Tente novamente em instantes.');
          sessaoRef.current = null; // deixa tentar de novo no turno seguinte
          throw error;
        });
    }
    return sessaoRef.current;
  }, [companyKey]);

  // Troca de empresa na mesma tela é outra denúncia: a sessão anterior não serve.
  useEffect(() => {
    sessaoRef.current = null;
    setSession(null);
    setSessionError('');
  }, [companyKey]);

  // Script publicado só pra reaproveitar o mesmo avatar (id/voiceId/
  // mouthAssetsPath/visemeEnabled) já configurado pra pesquisa-de-compliance
  // dessa empresa — a denúncia por avatar não tem checklist/roteiro próprio
  // no Firestore, o checklist vive no prompt do backend (buildDenunciaIntakeSystemPrompt).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const doc = await fetchScriptDocument({ companyKey, interviewTypeKey: INTERVIEW_TYPE_KEY, scriptVersion: SCRIPT_VERSION });
        if (!cancelled) setScriptDoc(doc);
      } catch (error) {
        console.warn('[EIA] Falha ao carregar script pra resolver o avatar da denúncia por avatar:', error?.message || error);
      }
    })();
    return () => { cancelled = true; };
  }, [companyKey]);

  // Resolve o acervo de boca (viseme) uma vez, se o avatar tiver visemeEnabled.
  useEffect(() => {
    if (!avatarCfg?.visemeEnabled || !avatarCfg?.mouthAssetsPath) return;
    let cancelled = false;
    getMouthAssets({
      mouthAssetsPath: avatarCfg.mouthAssetsPath,
      patchRect: avatarCfg.mouthAssetsPatchRect,
    })
      .then((assets) => { if (!cancelled) setMouthAssets(assets); })
      .catch((error) => console.warn('[EIA] Falha ao resolver mouthAssets da denúncia por avatar:', error?.message || error));
    return () => { cancelled = true; };
  }, [avatarCfg?.visemeEnabled, avatarCfg?.mouthAssetsPath, avatarCfg?.mouthAssetsPatchRect]);

  const callSendTurn = useCallback(async (responseValue) => {
    const sessao = await garantirSessao();
    const functions = getFunctions(app, 'southamerica-east1');
    const callable = httpsCallable(functions, 'sendDenunciaAvatarTurn');
    const { data } = await callable({
      company: companyKey,
      interviewTypeKey: INTERVIEW_TYPE_KEY,
      protocoloID: sessao.protocoloID,
      accessToken: sessao.accessToken,
      userResponse: responseValue || '',
    });
    return data;
  }, [companyKey, garantirSessao]);

  // Toca uma fala do avatar (saudação fixa com key estável, ou pergunta
  // dinâmica com key:null) — mesmo desenho de EntrevistaContinuaContainer.js.
  const speak = useCallback(async ({ text, key }) => {
    if (!text || !avatarCfg) return;
    setIsVideoLoading(true);
    try {
      const result = await getAvatarSpeech({
        interviewType: INTERVIEW_TYPE_KEY,
        company: companyKey,
        avatarId: avatarCfg.id,
        key: key || null,
        text,
        voiceId: avatarCfg.voiceId,
      });
      setAvatarSpeech(result);
      setVideoKey(Date.now());
    } catch (error) {
      console.warn('[EIA] Falha ao sintetizar fala da denúncia por avatar:', error?.message || error);
      setIsVideoLoading(false);
      setCanSend(true);
    }
  }, [companyKey, avatarCfg]);

  const requestNextTurn = useCallback(async (responseValue) => {
    setCanSend(false);
    setErrorText('');
    try {
      const turn = await callSendTurn(responseValue);
      setMessages((prev) => [...prev, { type: 'ia', text: turn.pergunta }]);
      setCurrentTurn({
        turnId: turn.turnId,
        tipoResposta: turn.tipoResposta,
        escala: turn.escala,
        encerrar: !!turn.encerrar,
      });
      if (turn.encerrar) {
        setIsFinished(true);
        await speak({ text: turn.pergunta, key: null });
        return;
      }
      await speak({ text: turn.pergunta, key: null });
    } catch (error) {
      console.error('[EIA] Falha ao avançar turno da denúncia por avatar:', error?.message || error);
      setErrorText('Não foi possível carregar a próxima pergunta. Tente novamente em instantes.');
      setCanSend(true);
    }
  }, [callSendTurn, speak]);

  // Abertura: saudação/aviso de confidencialidade é o MESMO texto fixo do
  // formulário legado (não gerado pelo LLM), tocado com key estável — só
  // depois desse áudio acabar (onEnded) é que o primeiro turno real é pedido
  // ao backend, e é ali que a sessão nasce. A saudação não depende de sessão
  // nenhuma — ela é local —, mas depende do gesto de começar: enquanto a pessoa
  // não clica em Iniciar, nada toca e nada é gravado. Ver a tela de abertura
  // abaixo e o comentário de garantirSessao.
  useEffect(() => {
    if (!iniciado || introStartedRef.current || !avatarCfg || !INTRO_TEXT) return;
    introStartedRef.current = true;
    setMessages([{ type: 'ia', text: INTRO_TEXT, first: true }]);
    speak({ text: INTRO_TEXT, key: 'intro-denuncia-avatar' });
  }, [iniciado, avatarCfg, speak]);

  const handleIntroOrTurnEnded = useCallback(() => {
    setIsVideoLoading(false);
    setIsPlayingState(false);
    if (isFinished) return;
    if (!currentTurn) {
      // acabou de tocar a saudação fixa — pede o primeiro turno real.
      requestNextTurn('');
      return;
    }
    setCanSend(true);
  }, [isFinished, currentTurn, requestNextTurn]);

  const handleSend = useCallback((value) => {
    const trimmed = (value ?? userInput).toString().trim();
    if (!trimmed || !canSend) return;
    setMessages((prev) => [...prev, { type: 'user', text: trimmed }]);
    setUserInput('');
    setCanSend(false);
    requestNextTurn(trimmed);
  }, [userInput, canSend, requestNextTurn]);

  // Deriva quickOptions/showTextInput a partir de tipoResposta/escala do
  // turno atual — mesma lógica de EntrevistaContinuaContainer.js.
  const tipoResposta = currentTurn?.tipoResposta || 'aberta';
  const isScaleQuestion = tipoResposta === 'nota' && !!currentTurn?.escala;
  const isYesNoQuestion = tipoResposta === 'sim_nao';
  const scaleMin = Number(currentTurn?.escala?.min ?? 1);
  const scaleMax = Number(currentTurn?.escala?.max ?? 5);
  const quickOptions = isScaleQuestion
    ? Array.from({ length: Math.max(1, scaleMax - scaleMin + 1) }, (_, i) => ({ label: String(scaleMin + i), value: scaleMin + i }))
    : (isYesNoQuestion
      ? [{ label: 'Sim', value: 'sim' }, { label: 'Não', value: 'nao' }]
      : []);
  const showTextInput = !(isScaleQuestion || isYesNoQuestion);
  const showMic = showTextInput;

  const videoProps = useMemo(() => ({
    ref: videoRef,
    key: videoKey,
    mode: 'viseme',
    avatarId: avatarCfg?.id,
    mouthAssets: avatarCfg?.visemeEnabled ? mouthAssets : null,
    audioSrc: avatarSpeech?.audioSrc || null,
    visemes: avatarSpeech?.visemes || [],
    autoPlay: true,
    preload: 'auto',
    onLoadedData: () => setIsVideoLoading(false),
    onWaiting: () => setIsVideoLoading(true),
    onError: () => setIsVideoLoading(false),
    // onPlay TAMBÉM limpa isVideoLoading (não só onLoadedData) — mesma
    // proteção contra corrida de timing documentada em
    // EntrevistaContinuaContainer.js.
    onPlay: () => { setIsPlayingState(true); setIsVideoLoading(false); },
    onEnded: handleIntroOrTurnEnded,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [videoKey, avatarCfg, mouthAssets, avatarSpeech, handleIntroOrTurnEnded]);

  // Abertura com gesto explícito, espelhando o canal por formulário
  // (Denuncia.js): a conversa — e portanto o protocolo — só começa quando a
  // pessoa clica. Até 19/09/2026 esta tela entrava direto na conversa, e um
  // robô de captura chegou a abrir um protocolo em produção só por carregar a
  // página. O gesto também resolve o bloqueio de autoplay do navegador, que
  // exige interação antes de tocar áudio.
  if (!iniciado) {
    return (
      <Abertura>
        <Cartao>
          <Marca>
            <span className="sigla" aria-hidden="true">{(companyKey || '?').charAt(0).toUpperCase()}</span>
            <span className="nome">{companyKey.toUpperCase()}</span>
            <span className="operado">Canal de denúncias · operado pela DUE</span>
          </Marca>
          <h1>Bem-vindo ao EIA Denúncias da empresa {companyKey.toUpperCase()}.</h1>
          <p>Você vai conversar com um atendente virtual. Nada é registrado antes de você começar.</p>
          <BotaoIniciar type="button" onClick={() => setIniciado(true)}><span>Iniciar</span></BotaoIniciar>
          <CanalAlternativo to={`/denuncia/${companyKey}`}>
            Prefere preencher um formulário?
          </CanalAlternativo>
        </Cartao>
      </Abertura>
    );
  }

  if (sessionError) {
    return (
      <InterviewLayout
        controlsProps={{ features: DEFAULT_FEATURES, avatars: [], selectedAvatar: null }}
        videoProps={{}}
        videoLoading={false}
        videoMissing
        messages={[]}
        showCaptions={false}
        isPlaying={false}
        isListening={false}
        isTranscribing={false}
        userInput=""
        onInputChange={() => {}}
        onSend={(e) => e?.preventDefault?.()}
        quickOptions={[]}
        showTextInput={false}
        showMic={false}
        sendDisabled
      >
        <div role="alert" style={{ color: '#b91c1c', textAlign: 'center', padding: '8px' }}>{sessionError}</div>
      </InterviewLayout>
    );
  }

  return (
    <InterviewLayout
      controlsProps={{
        features: DEFAULT_FEATURES,
        avatars: [],
        selectedAvatar: avatarCfg?.id,
      }}
      videoProps={videoProps}
      videoLoading={isVideoLoading}
      videoMissing={false}
      messages={messages}
      showCaptions
      isPlaying={isPlayingState}
      isListening={isListening}
      isTranscribing={isTranscribing}
      userInput={userInput}
      onInputChange={(e) => setUserInput(e.target.value)}
      onSend={(e) => { e?.preventDefault?.(); handleSend(); }}
      onToggleMic={() => toggleListening(setUserInput)}
      quickOptions={isFinished ? [] : quickOptions}
      quickActivationKey={currentTurn?.turnId || 'intro'}
      onQuickSelect={handleSend}
      quickDisabled={!canSend}
      showTextInput={!isFinished && showTextInput}
      showMic={!isFinished && showMic}
      sendDisabled={!canSend || !userInput.trim()}
      layoutOptions={{
        pageWidth: '500px',
        pageHeight: '720px',
        videoWidth: '800px',
        messageMaxHeight: '200px',
        messageMarginBottom: '90px',
      }}
    >
      {errorText && <div role="alert" style={{ color: '#b91c1c', textAlign: 'center', padding: '8px' }}>{errorText}</div>}
      {isFinished && session?.protocoloID && (
        <div style={{ textAlign: 'center', padding: '8px', color: '#fff' }}>
          Seu número de protocolo: <strong>{session.protocoloID}</strong>
        </div>
      )}
    </InterviewLayout>
  );
}

// Mesmos valores do canal por formulário (Denuncia.js), para as duas portas do
// canal de denúncia abrirem com a mesma cara.
/**
 * A abertura do canal por avatar, no LAYOUT OFICIAL (ver utils/layoutOficial.js),
 * espelhando a do canal por formulário em Denuncia.js — as duas são a mesma
 * porta, e precisam parecer a mesma porta.
 */
const Abertura = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${CORES.papel};
  padding: 32px 20px;
  box-sizing: border-box;
  color: ${CORES.tinta};
  font-family: ${TIPOGRAFIA.corpo};

  /* O cartão é o elemento, e não a página: o mesmo desenho do formulário. */
  & > * { max-width: 720px; }

  h1 {
    width: 100%;
    max-width: 720px;
    margin: 0;
    font-family: ${TIPOGRAFIA.titulo};
    font-size: 30px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -.02em;
    color: ${CORES.tinta};
    text-align: center;
  }

  p {
    font-family: ${TIPOGRAFIA.corpo};
    font-size: 16px;
    line-height: 1.55;
    color: ${CORES.apagado};
    text-align: center;
    max-width: 46ch;
    margin: 14px 0 0;
  }

  @media only screen and (max-width: 600px) {
    h1 { font-size: 24px; }
  }
`;

/** O cartão branco sobre o creme, que a Abertura centraliza. */
const Cartao = styled.div`
  width: 100%;
  max-width: 720px;
  background-color: ${CORES.superficie};
  border: 1px solid ${CORES.borda};
  border-radius: ${FORMA.raioCartao};
  padding: 44px 40px 36px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media only screen and (max-width: 600px) {
    padding: 32px 22px 28px;
  }
`;

const Marca = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid ${CORES.borda};

  .sigla {
    width: 34px; height: 34px; flex: 0 0 34px;
    border-radius: ${FORMA.raioBotao};
    background: ${CORES.tinta};
    color: ${CORES.superficie};
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px;
  }
  .nome { font-weight: 700; font-size: 16px; }
  .operado { margin-left: auto; font-size: 12px; color: ${CORES.apagado}; }

  @media only screen and (max-width: 600px) { .operado { display: none; } }
`;

const BotaoIniciar = styled.button`
  margin-top: 26px;
  min-width: 260px;
  max-width: 100%;
  height: 50px;
  padding: 0 32px;
  background-color: ${CORES.acao};
  border: none;
  border-radius: ${FORMA.raioBotao};
  cursor: pointer;

  span {
    color: #fff;
    font-family: ${TIPOGRAFIA.corpo};
    font-size: 17px;
    font-weight: 600;
  }
  &:hover { filter: brightness(1.12); }
  &:focus-visible { outline: 2px solid ${CORES.acao}; outline-offset: 3px; }
`;

const CanalAlternativo = styled(Link)`
  margin-top: 18px;
  color: ${CORES.acao};
  font-family: ${TIPOGRAFIA.corpo};
  font-size: 15px;
  text-decoration: none;
  cursor: pointer;

  &:hover { text-decoration: underline; }
`;
